import { ApiService } from '@/services';
import gql from 'graphql-tag';
import {
  QueryResponse,
  PaginatedResponse,
  Subscription,
  DataLoaderParams,
  Response,
  DeployDTO,
  Deployment,
  CreateDeploymentDTO,
  BulkUpgradeDTO,
  DeploymentDataLoaderParams,
  Credentials
} from '@/types';

class DeploymentService extends ApiService {
  public async deploy(input: DeployDTO): Promise<Response> {
    const mutation = gql`
      mutation deploy($input: DeployInput!) {
        deploy(input: $input) {
          status
          message
        }
      }
    `;
    return this.mutate({ mutation, variables: { input } }, 'deploy');
  }

  public async suspendDeployment(id: string): Promise<Response> {
    const mutation = gql`
      mutation suspendDeployment($id: ID!) {
        suspendDeployment(id: $id) {
          status
          message
        }
      }
    `;
    return this.mutate({ mutation, variables: { id } }, 'suspendDeployment');
  }

  public async resumeDeployment(id: string): Promise<Response> {
    const mutation = gql`
      mutation resumeDeployment($id: ID!) {
        resumeDeployment(id: $id) {
          status
          message
        }
      }
    `;
    return this.mutate({ mutation, variables: { id } }, 'resumeDeployment');
  }

  public async purgeDeployment(id: string): Promise<Response> {
    const mutation = gql`
      mutation purgeDeployment($id: ID!) {
        purgeDeployment(id: $id) {
          status
          message
        }
      }
    `;
    return this.mutate({ mutation, variables: { id } }, 'purgeDeployment');
  }

  public async create(dto: CreateDeploymentDTO): Promise<Deployment> {
    const mutation = gql`
      mutation createDeployment(
        $planId: ID!
        $subdomain: String!
        $environment: String!
        $initUserName: String!
        $initUserEmail: String!
      ) {
        createDeployment(
          planId: $planId
          subdomain: $subdomain
          environment: $environment
          initUserName: $initUserName
          initUserEmail: $initUserEmail
        ) {
          id
          app_id
          subdomain
          status
          environment
          service {
            id
            slug
            name
            dns_name
          }
          plan {
            id
            plan_name
          }
        }
      }
    `;
    return this.mutate({ mutation, variables: { ...dto } }, 'createDeployment');
  }

  public async bulkUpgrade(dto: BulkUpgradeDTO): Promise<Response> {
    const mutation = gql`
      mutation bulkUpgrade($versionId: ID!, $environment: String!) {
        bulkUpgrade(versionId: $versionId, environment: $environment) {
          status
          message
        }
      }
    `;
    return this.mutate({ mutation, variables: { ...dto } }, 'bulkUpgrade');
  }

  public async updateValues(
    deploymentId: string,
    values: string
  ): Promise<Response> {
    const mutation = gql`
      mutation updateValues($deploymentId: ID!, $values: String!) {
        updateValues(deploymentId: $deploymentId, values: $values) {
          status
          message
        }
      }
    `;
    return this.mutate(
      { mutation, variables: { deploymentId, values } },
      'updateValues'
    );
  }

  public async getValues(deploymentId: string): Promise<string> {
    const query = gql`
      query deploymentValues($deploymentId: ID!) {
        deploymentValues(deploymentId: $deploymentId)
      }
    `;
    return this.query<string>(
      { query, variables: { deploymentId } },
      'deploymentValues'
    );
  }

  public async getCredentials(deploymentId: string): Promise<Credentials> {
    const query = gql`
      query deploymentCredentials($deploymentId: ID!) {
        deploymentCredentials(deploymentId: $deploymentId) {
          clientId
          clientSecret
        }
      }
    `;
    return this.query<Credentials>(
      { query, variables: { deploymentId } },
      'deploymentCredentials'
    );
  }

  public async connectUpimport(
    upimportDeploymentId: string,
    upaffiliateDeploymentId: string
  ): Promise<Response> {
    const mutation = gql`
      mutation connectUpimport(
        $upimportDeploymentId: ID!
        $upaffiliateDeploymentId: ID!
      ) {
        connectUpimport(
          upimportDeploymentId: $upimportDeploymentId
          upaffiliateDeploymentId: $upaffiliateDeploymentId
        ) {
          status
          message
        }
      }
    `;
    return this.mutate(
      {
        mutation,
        variables: { upimportDeploymentId, upaffiliateDeploymentId }
      },
      'connectUpimport'
    );
  }

  public async getDeployments(
    params: DeploymentDataLoaderParams
  ): Promise<QueryResponse<PaginatedResponse<Deployment>>> {
    const query = gql`
      query deployments(
        $first: Int!
        $page: Int!
        $filters: DeploymentDataLoaderParams
      ) {
        deployments(first: $first, page: $page, filters: $filters) {
          data {
            id
            app_id
            subdomain
            status
            environment
            service {
              id
              slug
              name
              dns_name
            }
            version {
              id
              tag
            }
            cluster {
              id
              name
              provider
              zone
            }
            plan {
              id
              plan_name
            }
            subscription {
              id
              active
              customer {
                id
                name
                email
              }
            }
          }
          paginatorInfo {
            total
            perPage
            currentPage
            hasMorePages
          }
        }
      }
    `;
    return await this.query<QueryResponse<PaginatedResponse<Deployment>>>({
      query,
      variables: params
    });
  }
}

export const deploymentSvc: DeploymentService = new DeploymentService();
